import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Card, TONE, fmt } from "@/components/impact/ImpactWidgets";
import { CASE_BY_CLIENT, liveLinkForClient } from "@/lib/caseLinks";

const ATTR_TONE = { "Assisted": "emerald", "Human Assisted": "sky", "Organic": "amber", "Excluded": "zinc" };

export default function AttributionSection({ B, d }) {
  const [openEvidence, setOpenEvidence] = useState(null);
  const [spotlight, setSpotlight] = useState(false);
  const [searchParams] = useSearchParams();
  const caseParam = searchParams.get("case");
  const fromParam = searchParams.get("from");
  const focusIdx = useMemo(
    () => (caseParam ? B.attributionClients.findIndex((c) => CASE_BY_CLIENT[c.name] === caseParam) : -1),
    [B, caseParam]
  );

  useEffect(() => {
    if (focusIdx < 0) return;
    setOpenEvidence(focusIdx);
    const t1 = setTimeout(() => {
      document.querySelector(`[data-testid="attr-evidence-${focusIdx}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setSpotlight(true);
    }, 500);
    const t2 = setTimeout(() => setSpotlight(false), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [focusIdx]);

  return (
    <Card title="Outcome Attribution" sub="Every outcome belongs to exactly one category — click a contributing client for attribution evidence" testId="attribution-breakdown">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {B.attribution.map((a) => {
          const v = a.value + (a.deltaKey ? d[a.deltaKey] || 0 : 0);
          const changed = a.deltaKey && (d[a.deltaKey] || 0) > 0;
          return (
            <div key={a.key} className={`rounded-sm border p-3.5 ${changed ? "border-emerald-500/50 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/50"}`} data-testid={`attr-${a.key}`}>
              <div className={`text-lg font-bold font-mono ${changed ? "text-emerald-400" : "text-zinc-100"}`}>{fmt(v)}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">{a.label}</div>
              {a.key === "organic" && d.organicValue > 0 && (
                <div className="text-[9px] font-mono text-zinc-600 mt-1">incl. £3,000 GBP (illustrative conversion)</div>
              )}
            </div>
          );
        })}
      </div>
      <table className="w-full text-sm" data-testid="attribution-table">
        <thead>
          <tr className="border-b border-zinc-800 text-left">
            <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Client</th>
            <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Lifecycle issue</th>
            <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Intervention</th>
            <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Outcome</th>
            <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Value</th>
            <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Attribution</th>
          </tr>
        </thead>
        <tbody>
          {B.attributionClients.map((c, i) => (
            <Fragment key={c.name}>
              <tr
                data-testid={`attr-row-${i}`}
                onClick={() => setOpenEvidence(openEvidence === i ? null : i)}
                className="border-b border-zinc-800/60 hover:bg-zinc-800/40 cursor-pointer transition-colors duration-150"
              >
                <td className="py-2.5 text-xs font-semibold text-zinc-100">{c.name}</td>
                <td className="py-2.5 text-xs text-zinc-400">{c.issue}</td>
                <td className="py-2.5 text-xs text-zinc-400">{c.intervention}</td>
                <td className="py-2.5 text-xs text-zinc-300">{c.outcome}</td>
                <td className="py-2.5 text-xs font-mono text-zinc-200 text-right">{c.value}</td>
                <td className="py-2.5">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${TONE[ATTR_TONE[c.attribution]]}`}>
                    {c.attribution}
                  </span>
                </td>
              </tr>
              {openEvidence === i && (
                <tr className="border-b border-zinc-800/60">
                  <td colSpan={6} className="py-3">
                    <div className={`ml-2 border rounded-sm p-4 fade-slide-in transition-shadow duration-500 ${spotlight && focusIdx === i ? "border-emerald-400 bg-emerald-500/10 ring-2 ring-emerald-400/60 shadow-[0_0_20px_rgba(16,185,129,0.25)]" : "border-emerald-500/30 bg-emerald-500/5"}`} data-testid={`attr-evidence-${i}`}>
                      {fromParam === "live" && focusIdx === i && (
                        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="live-context-banner">
                          <Link
                            to={`/live?case=${caseParam}`}
                            data-testid="back-to-live-link"
                            className="text-[10px] font-mono font-semibold tracking-wider text-emerald-400 hover:text-emerald-300 transition-colors duration-150"
                          >
                            ← BACK TO LIVE OPERATIONS
                          </Link>
                          <span className="text-zinc-700 font-mono text-[10px] hidden sm:inline">|</span>
                          <span className="text-[10px] font-mono text-zinc-400">
                            Measured impact for this intelligence decision — Original Intelligence → Action → Outcome → Measured Impact
                          </span>
                        </div>
                      )}
                      <div className="text-[10px] font-mono tracking-widest text-emerald-400 uppercase mb-2">Attribution evidence — {c.name}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-[11px]">
                        <div><span className="text-zinc-600">O.N.E.Tech action: </span><span className="text-zinc-300 font-mono">{c.evidence.action}</span></div>
                        <div><span className="text-zinc-600">Client response: </span><span className="text-zinc-300 font-mono">{c.evidence.retry}</span></div>
                        <div><span className="text-zinc-600">Confirmation: </span><span className="text-zinc-300 font-mono">{c.evidence.confirm}</span></div>
                        <div><span className="text-zinc-600">Attribution window: </span><span className="text-zinc-300">{c.evidence.window}</span></div>
                        <div><span className="text-zinc-600">Previous human intervention: </span><span className="text-zinc-300">{c.evidence.prior}</span></div>
                        <div><span className="text-zinc-600">Classification: </span><span className="text-emerald-400 font-semibold">{c.attribution.toUpperCase()}</span></div>
                      </div>
                      {CASE_BY_CLIENT[c.name] && (
                        <div className="mt-3 pt-3 border-t border-emerald-500/20">
                          <Link
                            to={liveLinkForClient(c.name)}
                            data-testid={`view-intelligence-${i}`}
                            className="inline-flex items-center gap-1.5 text-[10px] font-mono font-semibold tracking-wider text-emerald-400 hover:text-emerald-300 transition-colors duration-150"
                          >
                            VIEW INTELLIGENCE — BEHIND THIS ATTRIBUTED OUTCOME <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                          </Link>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
